<template>
  <div>
    <!--
      手機：從下往上滑出的面板。
      QR Code 直接顯示（不用再點放大）、網址完整換行顯示，下面兩顆大按鈕：複製連結、系統分享。
    -->
    <!--
      retain-focus 關掉（跟電腦版一樣）：面板會把焦點搶回自己身上，
      複製時退回舊做法（暫時塞一個輸入框選取文字）就選不到，什麼都沒複製到。
    -->
    <v-bottom-sheet v-if="isMobile" v-model="proxy" width="100%" scrollable :retain-focus="false">
      <v-sheet ref="copyHost" class="share-sheet">
        <div class="share-sheet__handle" aria-hidden="true"></div>
        <div class="share-sheet__head">
          <div class="share-sheet__side"></div>
          <div class="share-sheet__title">{{ initTitle }}{{ titleSuffix }}</div>
          <v-btn class="share-sheet__side" icon aria-label="關閉" @click="proxy = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="share-sheet__body">
          <!-- 要登入才有專屬連結 -->
          <div v-if="!plain && !$store.state.signinStatus" class="share-sheet__signin">
            <v-icon size="40" color="primary">mdi-account-lock-outline</v-icon>
            <div class="share-sheet__signin-text">登入後就能取得會員專屬分享連結</div>
            <v-btn color="primary" depressed rounded large @click="GoRouter('SignIn')">登入</v-btn>
          </div>

          <template v-else>
            <!-- 分享卡：QR Code 直接顯示，對方掃了就到 -->
            <div class="share-card">
              <div class="share-card__qr">
                <VueQRCode :text="initUrl" :size="176" error-level="L" />
              </div>
              <div v-if="!plain" class="share-card__inviter">{{ qrInviterLine }}</div>
              <div v-if="initQrText" class="share-card__desc">{{ initQrText }}</div>
            </div>

            <!-- 網址完整顯示（會換行），不再擠在一個短短的輸入框裡 -->
            <div class="share-link">
              <div class="share-link__label">{{ shareHeading }}</div>
              <div class="share-link__url">{{ initUrl }}</div>
            </div>

            <div class="share-actions">
              <v-btn class="share-actions__btn" color="primary" depressed rounded large @click="copyLink">
                <v-icon left>{{ copied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                {{ copied ? "已複製" : "複製連結" }}
              </v-btn>
              <v-btn v-if="canNativeShare" class="share-actions__btn" color="primary" outlined rounded large @click="nativeShare">
                <v-icon left>mdi-share-variant</v-icon>
                分享到…
              </v-btn>
            </div>
          </template>
        </div>
      </v-sheet>
    </v-bottom-sheet>

    <!-- 電腦版：置中的對話框 -->
    <v-dialog v-if="!isMobile" v-model="proxy" width="448" persistent scrollable :retain-focus="false">
      <v-card ref="copyHost">
        <!--
          標題太長換行時，關閉鈕不能被擠到下一行：v-card-title 預設 flex-wrap: wrap，
          標題一寬就把 X 推下去。改成不換行、標題自己在中間那一格裡折行。
          左邊那格跟關閉鈕一樣寬，標題才會真的置中。
        -->
        <v-card-title class="share-dialog__title pt-4 pb-2">
          <div class="share-dialog__title-side"></div>
          <div class="share-dialog__title-text">{{ initTitle }}{{ titleSuffix }}</div>
          <v-btn class="share-dialog__title-side" @click="proxy = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="!plain && !$store.state.signinStatus">
          <div class="text-body-1 text--primary text-h6 d-flex justify-center align-center flex-wrap" style="min-height: 153px">
            <div>請先</div>
            <v-btn class="mx-2" color="primary" outlined @click="GoRouter('SignIn')">登入</v-btn>
            <div>已取得會員專屬分享連結</div>
          </div>
        </v-card-text>
        <v-card-text v-else>
          <div class="text-body-1 text--primary">
            <div class="font-weight-medium mb-2">{{ shareHeading }}</div>
            <div class="grey lighten-4 pa-4 rounded-lg">
              <div class="text-h6 blue--text text--darken-4 mb-3">
                <v-icon size="24" color="blue darken-4 ">mdi-link</v-icon>
                分享連結
              </div>
              <div class="mb-4 text-body-2">點擊複製按鈕後，分享給好友</div>
              <div class="d-flex">
                <v-text-field v-model="initUrl" filled hide-details="auto" append-icon="mdi-link" readonly></v-text-field>
                <v-btn height="48" color="primary" @click="CopyBoard">複製</v-btn>
              </div>
            </div>

            <div class="grey lighten-4 pa-4 rounded-lg mt-4">
              <div class="text-h6 blue--text text--darken-4 mb-3">
                <v-icon size="24" color="blue darken-4 ">mdi-qrcode</v-icon>
                二維條碼
              </div>
              <div class="d-flex align-center">
                <v-img :src="qrcodeImg" max-width="65" max-height="65" contain class="mr-4"></v-img>
                <v-btn @click="qrDialog = true" color="primary" large>點擊放大</v-btn>

                <v-dialog v-model="qrDialog" width="400" attach="body">
                  <v-card>
                    <v-card-title class="text-h6 2">
                      {{ qrTitle }}
                      <v-spacer></v-spacer>
                      <v-btn icon @click="qrDialog = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>

                    <div class="px-4">
                      <div align="center">
                        <div style="height: 24px"></div>
                        <VueQRCode :text="initUrl" :size="200" error-level="L" class="py-4"></VueQRCode>
                        <div style="font-size: 24px; font-weight: 500; padding: 16px">
                          <!-- 純分享沒有邀請人，只留下面那句 -->
                          <div v-if="!plain">{{ qrInviterLine }}</div>
                          <div v-if="initQrText">{{ initQrText }}</div>
                        </div>
                      </div>
                    </div>

                    <v-card-actions class="pa-4">
                      <v-spacer></v-spacer>
                      <v-btn @click="qrDialog = false">關閉</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { saveRedirectUrl } from "@/utils/redirectHelper";
import VueQRCode from "vue-qrcode-component";

export default {
  name: "ShareDialog",
  props: {
    value: { type: Boolean, default: false }, // ← v-model 對應的 prop
    url: { type: String, default: null },
    title: { type: String, default: null },
    qrText: { type: String, default: null },
    prefixText: { type: String, default: null },
    /**
     * 純分享：只是把一個網址分享出去，與小芋頭會員身分無關。
     *
     * 給對外品牌站的自訂頁用（見 ShortcodeShare 的同名 prop）。
     * 預設 false —— 平台自己的分享一律維持原本「登入後拿專屬連結」的行為，
     * 那是分潤機制的入口，不能因為這個新增的模式而改變。
     */
    plain: { type: Boolean, default: false },
    /**
     * 標題後面接的字。平台自己的分享維持「會員邀請分享」這種寫法；
     * 自訂頁的分享按鈕傳空字串 —— 設計師填的標題就是要顯示的完整標題，
     * 「黑牛角旅店」後面多出「分享」兩個字是錯的。
     */
    titleSuffix: { type: String, default: "分享" },
    /**
     * 自訂頁分享按鈕的分享文字（複製與系統分享時放在網址前面）。
     *
     * 有給才走新的格式，沒給（平台自己的分享）完全照舊：
     *   純分享       {分享文字} {網址}
     *   要登入的分享 {登入者}推薦你-{分享文字} {網址}
     */
    shareText: { type: String, default: null },
    /**
     * 「請先登入」要去的完整網址。自訂網域上沒有會員功能（沒有登入頁），
     * 要登入只能回平台網域，由呼叫端給出平台上的同一頁。沒給就走站內的登入頁。
     */
    signInHref: { type: String, default: null },
    /**
     * 登入完要回到哪裡。沒給就是目前網址；分享按鈕會給帶著定位參數的網址，
     * 登入回來後自動捲到那顆按鈕。
     */
    returnUrl: { type: String, default: null },
  },
  components: {
    VueQRCode,
  },
  data() {
    return {
      qrcodeImg: require("@/assets/image/serivceIcon/qrcode_icon.svg"),
      qrDialog: false,
      // 手機版「複製連結」按下去後短暫顯示「已複製」
      copied: false,
      copiedTimer: null,
    };
  },
  computed: {
    /** 手機：從下往上滑出的面板；電腦維持置中的對話框 */
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    /** 系統分享（LINE、訊息…）：手機瀏覽器大多支援，不支援就不放那顆按鈕 */
    canNativeShare() {
      return typeof navigator !== "undefined" && typeof navigator.share === "function";
    },
    proxy: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }, // ← v-model 對應的事件
    },
    initUrl() {
      if (this.url !== null) return this.url;

      // 純分享沒填 url 就分享「當前這一頁」；退回平台首頁只會讓人一頭霧水
      if (this.plain) return window.location.href;

      // 優先從 Store 取得推薦碼，若無則嘗試從 localStorage 讀取 (相容性考慮)
      const emailCode = this.$store.state.username || localStorage.getItem("_emailEncrypt") || "";

      return `${process.env.VUE_APP_MAIN_BASE_URL}?refc=${emailCode}`;
    },
    /** 連結區塊的標題。純分享時不掛人名 —— 訪客沒登入，那裡會是空的 */
    shareHeading() {
      return this.plain ? "分享連結" : `${this.$store.state.userRealName} 的分享連結`;
    },
    initTitle() {
      let defaultTitle = "會員邀請";
      return this.title === null ? defaultTitle : this.title;
    },
    /*
      放大的 QR Code 視窗。

      自訂頁（子站）的分享按鈕會傳 shareText，那時這裡要跟著分享文字走 ——
      「二維條碼邀請」「加入小芋頭平台會員」是平台會員邀請的說法，
      黑牛角這種品牌站分享的是它自己的網站，不是邀請加入小芋頭。
      平台自己的分享不傳 shareText，維持原本的字。
    */
    qrTitle() {
      return this.shareText !== null ? "二維條碼" : "二維條碼邀請";
    },
    initQrText() {
      if (this.qrText !== null) return this.qrText;
      if (this.shareText !== null) return this.shareText;
      return "加入小芋頭平台會員";
    },
    /** QR 上方那行：要登入的分享才有。子站的說法與複製文字一致（{登入者}推薦你-{分享文字}） */
    qrInviterLine() {
      const name = this.$store.state.userRealName;
      return this.shareText !== null ? `${name} 推薦你` : `${name} 邀請您`;
    },
  },

  watch: {
    // 手機面板開著時藏起小芋頭助手，不然它會浮在面板右下角（見 GlobalAgentNotification）
    value(open) {
      if (this.isMobile) document.body.classList.toggle("taro-assistant-hidden", open);
    },
  },
  beforeDestroy() {
    clearTimeout(this.copiedTimer);
    if (this.value) document.body.classList.remove("taro-assistant-hidden");
  },

  methods: {
    GoRouter(url) {
      if (url === "SignIn") {
        if (this.signInHref) {
          window.location.href = this.signInHref;
          return;
        }
        if (this.returnUrl) localStorage.setItem("redirectAfterLogin", this.returnUrl);
        else saveRedirectUrl();
        // 取得當前網址上的 refc（同名多值只取第一個）
        const raw = this.$route.query.refc;
        const refc = Array.isArray(raw) ? raw[0] : raw;

        // 只有在 refc 有值時才帶上去
        const query = {
          status: "signin",
          from: "lt",
          ...(typeof refc === "string" && refc.trim() !== "" ? { refc } : {}),
        };

        this.$router.push({ name: "SignIn", query });
        return;
      }

      this.$router.push({ name: url });
    },
    /** 複製與系統分享共用的文字：{前綴} {網址} */
    buildShareText() {
      /*
          純分享的前綴只用呼叫端給的文案（多半是頁面標題），沒有就用網頁標題。
          預設那句「好友 邀請您加入小芋頭平台會員」在品牌站上是錯的 ——
          分享的是那個子站的頁面，不是小芋頭會員。
      */
      const name = this.$store.state.userRealName || "好友";
      // 任何複製的網址前面都要有一段文字（2026-10-28）：呼叫端沒給文案時，用這一頁的網頁標題
      const fallback = (document.title || "").trim() || "推薦你看看這個網頁";
      let preWord;
      if (this.shareText !== null) {
        // 自訂頁的分享按鈕：純分享只放分享文字；要登入的分享加上「誰推薦你」
        const body = (this.shareText || "").trim() || fallback;
        preWord = this.plain ? body : `${name}推薦你-${body}`;
      } else {
        preWord = this.plain ? (this.prefixText || "").trim() || fallback : `${name} ${this.prefixText || "邀請您加入小芋頭平台會員"}`;
      }

      const url = this.addOpenExternalBrowser(this.initUrl);
      return { preWord, url, text: `${preWord} ${url}` };
    },
    /**
     * 寫進剪貼簿。先用瀏覽器內建的 navigator.clipboard —— 不用選取文字，對話框搶焦點也不影響；
     * 不支援（非 https、舊瀏覽器、部分 App 內的 WebView）或被拒時，才退回 vue-clipboard2，
     * 並把它暫時塞的輸入框放在對話框裡面，不然焦點一離開對話框，選取就不見了。
     */
    async writeClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          return;
        } catch (e) {
          /* 退回下面的做法 */
        }
      }
      const host = this.$refs.copyHost;
      await this.$copyText(text, (host && host.$el) || document.body);
    },
    copyLink() {
      const { text } = this.buildShareText();
      this.writeClipboard(text)
        .then(() => {
          this.$notify({ content: "複製成功！開始分享", status: "success" });
          this.copied = true;
          clearTimeout(this.copiedTimer);
          this.copiedTimer = setTimeout(() => (this.copied = false), 2000);
        })
        .catch(() => {
          this.$notify({ content: "複製失敗，請手動複製連結", status: "error" });
        });
    },
    nativeShare() {
      if (!this.canNativeShare) return;
      const { preWord, url } = this.buildShareText();
      navigator
        .share({
          title: this.plain ? document.title || this.initTitle : "小芋頭平台",
          text: preWord,
          url: url,
        })
        .then(() => {})
        .catch(() => {});
    },
    /** 電腦版的「複製」：複製後順便叫出系統分享（維持原本的行為） */
    CopyBoard() {
      this.copyLink();
      this.nativeShare();
    },

    addOpenExternalBrowser(u) {
      try {
        const url = new URL(u, window.location.origin);
        url.searchParams.set("openExternalBrowser", "1");
        return url.toString(); // 不對整段做 encodeURIComponent
      } catch {
        const sep = u.includes("?") ? "&" : "?";
        return `${u}${sep}openExternalBrowser=1`;
      }
    },
  },
};
</script>

<style scoped>
.share-dialog__title {
  flex-wrap: nowrap;
  align-items: flex-start;
}

/* 左邊的空格與右邊的關閉鈕同寬（v-btn icon 是 36px），中間的標題才會置中 */
.share-dialog__title-side {
  flex: 0 0 36px;
}

.share-dialog__title-text {
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 8px 0;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
}

/* ───── 手機：底部面板 ───── */
.share-sheet {
  border-radius: 20px 20px 0 0 !important;
  max-height: 92vh;
  overflow-y: auto;
  padding-bottom: env(safe-area-inset-bottom);
}

.share-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 8px auto 0;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.18);
}

/* 左右兩格同寬，標題才會真的置中 */
.share-sheet__head {
  display: flex;
  align-items: center;
  padding: 4px 8px 0;
}

.share-sheet__side {
  flex: 0 0 36px;
}

.share-sheet__title {
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.4;
}

.share-sheet__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px 20px 20px;
}

.share-sheet__signin {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0 8px;
  text-align: center;
}

.share-sheet__signin-text {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.7);
}

/* 分享卡：淡紫底，QR Code 放在白色圓角方塊裡 */
.share-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f1ecff 0%, #f8f6ff 100%);
  text-align: center;
}

.share-card__qr {
  padding: 12px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(80, 50, 160, 0.1);
  line-height: 0;
}

.share-card__inviter {
  margin-top: 14px;
  font-size: 17px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}

.share-card__desc {
  margin-top: 2px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.share-link__label {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
}

/* 網址整串顯示，太長就換行 */
.share-link__url {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f5f7;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.75);
  word-break: break-all;
  user-select: all;
}

.share-actions {
  display: flex;
  gap: 10px;
}

.share-actions__btn {
  flex: 1 1 0;
  min-width: 0 !important;
}
</style>
